import {createContext, use, useMemo, useRef, useState, type ReactNode, type RefObject} from 'react'

export type DockedPanelType = 'comments' | 'alerts' | 'overview' | null

interface DockedPanelActions {
  openPanel: (panel: Exclude<DockedPanelType, null>) => void
  closePanel: () => void
  togglePanel: (panel: Exclude<DockedPanelType, null>) => void
}

export interface DockedPanelFocusRefs {
  /** Ref to the close button inside DockedSidePanelContent */
  closeButtonRef: RefObject<HTMLButtonElement | null>
  /** Ref to the element that should receive focus when the panel closes */
  returnFocusRef: RefObject<HTMLElement | null>
}

// Split contexts to prevent unnecessary re-renders:
// - Components that only need to trigger actions won't re-render when panel state changes
// - Components that need to read state will subscribe to state context
export const DockedPanelStateContext = createContext<DockedPanelType>(null)

const defaultActions: DockedPanelActions = {
  openPanel: () => undefined,
  closePanel: () => undefined,
  togglePanel: () => undefined,
}

export const DockedPanelDispatchContext = createContext<DockedPanelActions>(defaultActions)

const defaultFocusRefs: DockedPanelFocusRefs = {
  closeButtonRef: {current: null},
  returnFocusRef: {current: null},
}

export const DockedPanelFocusContext = createContext<DockedPanelFocusRefs>(defaultFocusRefs)

interface DockedPanelProviderProps {
  children: ReactNode
  isOnSupportedRoute?: boolean
}

export function DockedPanelProvider({children, isOnSupportedRoute = false}: DockedPanelProviderProps) {
  const [dockedPanel, setDockedPanel] = useState<DockedPanelType>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  // Memoize actions object to keep dispatch context value stable
  // This ensures components using only useDockedPanelActions() won't re-render on state changes
  const actions = useMemo<DockedPanelActions>(
    () => ({
      openPanel: (panel: Exclude<DockedPanelType, null>) => setDockedPanel(panel),
      closePanel: () => setDockedPanel(null),
      togglePanel: (panel: Exclude<DockedPanelType, null>) =>
        setDockedPanel(current => (current === panel ? null : panel)),
    }),
    [],
  )

  // Stable ref object - refs don't change identity so this is safe to memoize once
  const focusRefs = useMemo<DockedPanelFocusRefs>(() => ({closeButtonRef, returnFocusRef}), [])

  // Only expose the panel state when on a supported route (files changed).
  // On other routes the panel is effectively closed, hiding the sidebar.
  const effectivePanel = isOnSupportedRoute ? dockedPanel : null

  return (
    <DockedPanelStateContext value={effectivePanel}>
      <DockedPanelDispatchContext value={actions}>
        <DockedPanelFocusContext value={focusRefs}>{children}</DockedPanelFocusContext>
      </DockedPanelDispatchContext>
    </DockedPanelStateContext>
  )
}

/** Returns the currently open docked panel name, or null if closed */
export function useDockedPanel(): DockedPanelType {
  return use(DockedPanelStateContext)
}

/** Returns memoized actions to control the docked panel - stable reference, won't cause re-renders */
export function useDockedPanelActions(): DockedPanelActions {
  return use(DockedPanelDispatchContext)
}

/** Returns whether any docked panel is currently open on a supported route (files changed) */
export function useIsDockedPanelOpen(): boolean {
  return use(DockedPanelStateContext) !== null
}

/** Returns shared focus refs for managing focus between panel openers and the close button */
export function useDockedPanelFocusRefs(): DockedPanelFocusRefs {
  return use(DockedPanelFocusContext)
}
